import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { companyApi } from '@/api';
import { Alert, Button, Input, Modal } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

import { formatMoney } from './money';
import { CHANGE, planChangeCopy, startsNow } from './planChange';

/**
 * Confirming a plan change, with somewhere to put a discount code.
 *
 * The code is checked before it is spent, and the check is a separate step on
 * purpose. Applying a code and finding out what it was worth from the invoice
 * afterwards is the version of this that generates support tickets: the
 * customer sees the price they will pay *before* agreeing to it, and the
 * preview writes nothing — the code is only redeemed by the change itself.
 *
 * What comes back is prices, never the coupon's terms. How many redemptions a
 * campaign has left is not a customer's business, and the endpoint does not
 * tell them.
 *
 * `kind` (from `plan.change`) decides what the dialog says about timing:
 * "Starts now" with an invoice, or "Starts on <date>, when your current
 * period ends" with nothing billed -- and only a change billed now offers the
 * code field, since the server refuses a code on one that is not.
 */
export function PlanChangeDialog({
  plan,
  open,
  isLoading,
  onCancel,
  onConfirm,
  kind = CHANGE.UPGRADE,
  currentName,
  periodEnd,
  replacesUnpaid = false,
}) {
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(null);

  // A dialog reopened for a different plan must not carry the last plan's
  // priced discount with it -- that number was for a different price.
  useEffect(() => {
    if (!open) {
      setCode('');
      setApplied(null);
    }
  }, [open]);

  const preview = useMutation({
    mutationFn: () => companyApi.previewCoupon(code.trim(), plan.code),
    onSuccess: (result) => setApplied(result),
    onError: () => setApplied(null),
  });

  if (!plan) return null;
  const billsNow = startsNow(kind);
  // A code is offered for any paid plan. With a scheduled change it is checked
  // now and kept for the new plan's first bill.
  const takesCode = (plan.price_cents ?? 0) > 0;
  const copy = planChangeCopy(kind, { planName: plan.name, currentName, periodEnd });

  return (
    <Modal
      open={open}
      title={`Move to ${plan.name}?`}
      description={
        replacesUnpaid
          ? `${copy.description} The unpaid invoice for ${currentName ?? 'your current plan'} is cancelled.`
          : copy.description
      }
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            isLoading={isLoading}
            onClick={() => onConfirm(plan.code, applied ? applied.code : undefined)}
          >
            Confirm
          </Button>
        </>
      }
    >
      {takesCode && (
        <div className="space-y-4">
          <div>
            {/* The hint sits under the whole row, not inside the field, so the
                button lines up with the box rather than with the hint. */}
            <div className="flex items-end gap-2">
              <Input
                label="Discount code"
                placeholder="e.g. WELCOME20"
                wrapperClassName="min-w-0 flex-1"
                className="h-9 uppercase placeholder:normal-case"
                autoComplete="off"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  // The old price no longer describes the code in the box.
                  setApplied(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && code.trim().length >= 3) {
                    event.preventDefault();
                    preview.mutate();
                  }
                }}
              />
              <Button
                variant="secondary"
                className="h-9 shrink-0"
                disabled={code.trim().length < 3 || preview.isPending}
                isLoading={preview.isPending}
                onClick={() => preview.mutate()}
              >
                Apply
              </Button>
            </div>
            <p className="text-ink-muted mt-1.5 text-xs">
              {billsNow
                ? 'Optional. Have a code? Apply it to see the new price before you confirm.'
                : `Optional. It is checked now and used on ${plan.name}'s first bill, when the change happens.`}
            </p>
          </div>

          {preview.isError && (
            <Alert tone="warning">{errorMessage(preview.error, 'That code is not valid')}</Alert>
          )}

          {applied && (
            <Alert tone="success" title={`${applied.code} applied`}>
              {applied.description} You will pay{' '}
              <strong>{formatMoney(applied.total_cents, applied.currency)}</strong> instead of{' '}
              {formatMoney(applied.plan_price_cents, applied.currency)}
              {applied.recurring
                ? billsNow
                  ? ', on this and every renewal.'
                  : `, from ${plan.name}'s first bill and on every renewal.`
                : billsNow
                  ? ' on your first bill.'
                  : ` on ${plan.name}'s first bill. Nothing is billed today.`}
            </Alert>
          )}
        </div>
      )}
    </Modal>
  );
}
